import React, { useState, useEffect, useRef } from "react";
import ExtendedButton from "@src/stories/elements/ExtendedButton/ExtendedButton";
import { ExtendedNotifyModal } from "./ExtendedNotifyModal";
import { ExtendedUpgradeModal } from "./ExtendedUpgradeModal";
import { useCart } from "frontend/js/useShopifyCart";


const getMonogramValues = () => {
  const monogram1Input = document.querySelector('[data-name="monogram-text-1"]') as HTMLInputElement | null;
  const monogram2Input = document.querySelector('[data-name="monogram-text-2"]') as HTMLInputElement | null;
  const noMonogramCheckbox = document.querySelector('input[name="no_monogram"]') as HTMLInputElement | null;
  const isMonogram2Required = !!monogram2Input && !monogram2Input.disabled;
  
  const monogram1Value = monogram1Input?.value || '';
  const monogram2Value = monogram2Input?.value || '';
  const isNoMonogramChecked = noMonogramCheckbox?.checked || false;

  return {
    monogram1Value,
    monogram2Value,
    isMonogram2Required,
    hasMonogramInputs: !!monogram1Input,
    isNoMonogramChecked
  };
};

const checkMonogramValidity = (setIsDisabled: (disabled: boolean) => void, requiredMonogram: boolean = false) => {
  const { monogram1Value, monogram2Value, isMonogram2Required, hasMonogramInputs, isNoMonogramChecked } = getMonogramValues();
  
  if (!hasMonogramInputs) {
    setIsDisabled(false);
    return;
  }

  if (requiredMonogram) {
    // Required monogram: strict validation (cannot be empty or 'N/A')
    const isValid = monogram1Value.trim() !== '' &&
      monogram1Value !== 'N/A' &&
      (!isMonogram2Required || (isMonogram2Required && monogram2Value.trim() !== '' && monogram2Value !== 'N/A'));
    
    setIsDisabled(!isValid);
  } else {
    // Optional monogram: valid if user fills fields OR checks no_monogram checkbox
    const hasValidMonogramValues = monogram1Value.trim() !== '' &&
      monogram1Value !== 'N/A' &&
      (!isMonogram2Required || (isMonogram2Required && monogram2Value.trim() !== '' && monogram2Value !== 'N/A'));
    
    const isValid = hasValidMonogramValues || isNoMonogramChecked;
    setIsDisabled(!isValid);
  }
};

const AddToCartButton = ({
  quantity,
  addToCartUrl,
  variant,
  buttonLabel,
  redirect,
  onClose,
  buttonType,
  delivery_date,
  isDisabled,
  setIsDisabled,
  product,
  isGiftCard = false,
  delivery_date_end,
  requiredMonogram,
  chilled,
  florally,
  archie,
  nottage_inclusive_url,
  translations
}) => {
  const addForm = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const { cart } = useCart();
  
  useEffect(() => {
    setIsLoading(false);
  }, [cart]);

  useEffect(() => {
    if (requiredMonogram) {
      checkMonogramValidity(setIsDisabled, true);
  
      const intervalId = setInterval(() => {
        checkMonogramValidity(setIsDisabled, true);
      }, 500);
      
      return () => clearInterval(intervalId);
    } else {
      // For optional monogram, run validation to check for no_monogram checkbox
      checkMonogramValidity(setIsDisabled, false);
      
      const intervalId = setInterval(() => {
        checkMonogramValidity(setIsDisabled, false);
      }, 500);
      
      return () => clearInterval(intervalId);
    }
  }, [requiredMonogram]);


  const itemIds = cart.items.map(item => item.id);

  const getUploadedImageUrl = () => {
    const uploadedContainer = document.querySelector('.tcustomizer-photo-uploaded');
    if (uploadedContainer) {
      const img = uploadedContainer.querySelector('img') as HTMLImageElement | null;
      if (img?.src) return img.src;
    }

    const photoImage = document.querySelector('.tcustomizer-photo-image') as HTMLImageElement | null;
    if (photoImage?.src) return photoImage.src;
    
    return null;
  };

  const dispatchCustomEvent = (eventName, detail) => {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  };

  return (
    <>
      {nottage_inclusive_url ? (
        <a href={nottage_inclusive_url}  target="_blank" className={`button button-primary no-underline`} >
            {translations?.nottage_inclusive || ''}
        </a>
      ) : (
        <ajax-cart-product-form>
          <form
            action={addToCartUrl}
            noValidate
            ref={addForm}
            data-prodify-product-form
          >
            <input type="hidden" name="id" value={variant && variant.id} />
            <input type="hidden" name="quantity" value={quantity} />

            <div>
              <button
                className={`button button-primary`}
                disabled={isLoading || isDisabled}
                onClick={() => { 
                  const photoUrl = getUploadedImageUrl();
                  dispatchCustomEvent("add-to-cart", { 
                    id: variant.id, 
                    quantity: quantity, 
                    title: product.title, 
                    price: Number(product.price)/100, 
                    type: variant.title, 
                    ids: itemIds, 
                    delivery_date: delivery_date, 
                    delivery_date_end: delivery_date_end, 
                    chilled: chilled, 
                    isGiftCard: isGiftCard, 
                    florally: florally, 
                    archie: archie, 
                    photoUrl 
                  });
                  setIsLoading(true);
                  onClose(); 
                }}
              >
                {isLoading ? 'Adding To Cart...' : buttonLabel}
              </button>
            </div>

            <div
              data-ajax-cart-errors="form"
              className="absolute text-[10px] text-mb-red"
            ></div>
          </form>
        </ajax-cart-product-form>
      )}
    </>
  );
};

const ProductAddToCart = ({ product, routes, translations, localization }) => {
  const { variants, delivery_date, delivery_date_end, siblings, is_sellable, hamper_products_size, metafields, show_quantity_selector, add_to_cart_error } = product;
  const { add_to_cart_url } = routes;
  const [productStatus, setProductStatus] = useState("add");
  const [productQuantity, setProductQuantity] = useState(1);
  const [buttonLabel, setButtonLabel] = useState(translations?.add_to_cart);
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  
  const { chilled, florally, archie, required_monogram, nottage_inclusive_url } = metafields;

  const standardVariant = variants.find((obj) => obj.title === "Standard");
  const preOrderVariant = variants.find((obj) => obj.title === "Pre-Order");
  const filteredSiblings = siblings && siblings.sibling_products.filter(
    (prod) =>
      Number(product.price) < Number(prod.price) && product.title != prod.title,
  );
  const currentVariant =
    productStatus == "pre" ? preOrderVariant : standardVariant;

  // is sellable metafield is used to determine if non hamper products are sellable, if hamper = sellable automatically
  const isSellable = hamper_products_size > 0 ? true : is_sellable;

  const handleUpgradeClose = () => {
    document.querySelector(".pdp-details")?.classList.remove("z-40");
    document.querySelector("#sticky-bar")?.classList.remove("modal-open");
    document.body.classList.remove("modal-open");
    setIsUpgradeOpen(false);
  };

  const handleUpgradeOpen = () => {
    document.querySelector(".pdp-details")?.classList.add("z-40");
    document.querySelector("#sticky-bar")?.classList.add("modal-open");
    document.body.classList.add("modal-open");
    setIsUpgradeOpen(true);
  };

  useEffect(() => {
    if (delivery_date != "" && delivery_date_end != "") {
      if ( preOrderVariant && JSON.parse(preOrderVariant?.available)) {
        setProductStatus("pre");
        setButtonLabel(translations?.pre_order);
      } else {
        setProductStatus("notify_pre");
        setButtonLabel(translations?.notify_me_pre);
      }
    } else {
    
      if ( standardVariant && JSON.parse(standardVariant?.available)) {
        setProductStatus("add");
        setButtonLabel(translations?.add_to_cart);
      } else {
        setProductStatus("notify");
        setButtonLabel(translations?.notify_me);
      }
    }
  }, []);


  return (
    <>


      {isSellable && 
       (<div className="flex flex-col lg:flex-row items-center justify-between gap-[16px] py-2">
        { !product.is_giftcard && 
         ( <>
         { show_quantity_selector &&
          <div className="quantity-row quantity-field w-full lg:w-[190px] h-[48px] ">
          <div className="w-full h-full flex items-center justify-center">
            <span
              className="quantity-field__button text-[24px] px-[4px] py-[8px] icon-minus"
              onClick={() => {
                setProductQuantity(
                  productQuantity - 1 > 0 ? productQuantity - 1 : 0,
                );
              }}
            ></span>
            <input
              className="quantity-field__input"
              type="text"
              id="quantity-field__input"
              value={productQuantity}
            />
            <span
              className="quantity-field__button text-[24px] px-[4px] py-[8px] icon-plus"
              onClick={() => {
                setProductQuantity(productQuantity + 1);
              }}
            ></span>
          </div>
          </div>
         }

        <div className="product___add-to-cart w-full block">
          <div className="add-to-cart-button">
            {productStatus == "notify" || productStatus == "notify_pre" ? (
              <ExtendedButton
                style="primary"
                label={buttonLabel}
                icon="bell"
                onClick={() => {
                  setIsNotifyOpen(true);
                }}
              />
            ) : filteredSiblings && filteredSiblings.length > 0 ? (
              <ExtendedButton
                style="primary"
                label={buttonLabel}
                onClick={() => handleUpgradeOpen()}
              />
            ) : (
              <AddToCartButton
                quantity={productQuantity}
                buttonType={"primary"}
                addToCartUrl={add_to_cart_url}
                buttonLabel={buttonLabel}
                variant={currentVariant}
                product={product}
                requiredMonogram={required_monogram}
                isDisabled={isDisabled}
                setIsDisabled={setIsDisabled}
                delivery_date={delivery_date}
                delivery_date_end={delivery_date_end}
                nottage_inclusive_url={nottage_inclusive_url}
                chilled={chilled}
                florally={florally}
                archie={archie}
                redirect={"close"}
                translations={translations}
                onClose={() => {}}
              />
            )}
          </div>
        </div> </>)
        }

        { product.is_giftcard && 
          ( 
            <>
              <div className="quantity-row quantity-field w-full lg:w-[190px] h-[48px] ">
                <div className="w-full h-full flex items-center justify-center">
                  <span
                    className="quantity-field__button text-[24px] px-[4px] py-[8px] icon-minus"
                    onClick={() => {
                      setProductQuantity(
                        productQuantity - 1 > 0 ? productQuantity - 1 : 0,
                      );
                    }}
                  ></span>
                  <input
                    className="quantity-field__input"
                    type="text"
                    id="quantity-field__input"
                    value={productQuantity}
                  />
                  <span
                    className="quantity-field__button text-[24px] px-[4px] py-[8px] icon-plus"
                    onClick={() => {
                      setProductQuantity(productQuantity + 1);
                    }}
                  ></span>
                </div>
              </div>

              <div className="product___add-to-cart w-full block">
                <div className="add-to-cart-button">
                    <AddToCartButton
                      quantity={productQuantity}
                      buttonType={"primary"}
                      addToCartUrl={add_to_cart_url}
                      buttonLabel={translations?.add_to_cart}
                      variant={variants[0]}
                      product={product}
                      isGiftCard
                      requiredMonogram={required_monogram}
                      isDisabled={isDisabled}
                      setIsDisabled={setIsDisabled}
                      delivery_date={delivery_date}
                      delivery_date_end={delivery_date_end}
                      chilled={chilled}
                      florally={florally}
                      archie={archie}
                      translations={translations}
                      nottage_inclusive_url={nottage_inclusive_url}
                      redirect={"close"}
                      onClose={() => {}}
                    />
                </div>
              </div> 
            </>
          )
        }
        <ExtendedNotifyModal
          modal_id={"notify_me_modal"}
          isOpen={isNotifyOpen}
          onClose={() => setIsNotifyOpen(false)}
          product_name={product.title}
          product_id={
            productStatus == "notify" ? standardVariant?.id : preOrderVariant?.id
          }
        />
        <ExtendedUpgradeModal
          modal_id={"upgrade_me_modal"}
          isOpen={isUpgradeOpen}
          onClose={() => handleUpgradeClose()}
          product={product}
          add_to_cart_url={add_to_cart_url}
          translations={translations}
          localization={localization}
          delivery_date={delivery_date}
          delivery_date_end={delivery_date_end}
          chilled={chilled}
          florally={florally}
          archie={archie}
          variant={currentVariant}
          productQuantity={productQuantity}
        />
      </div> )}

      {isSellable && !product.is_giftcard && isDisabled && (
        <div className="text-sm text-mb-red mb-4">
          { add_to_cart_error || translations.add_to_cart_error }
        </div>
      )}
    </>
  );
};

export default ProductAddToCart;
